import { computed, observable, action } from "mobx";
import { create, persist } from "mobx-persist";
import { API } from "../Network/API";
import { APIClient } from "network-stapler";
import utilsFunctions from "../Utils/ModuleHelpers";
import {
	IGeneralModule,
	IModule,
	IModuleType,
	IOption,
	IProduct,
	IProductOptions,
	IStep,
	IDeliverTypeElement,
	IIncompatibleModule,
} from "../Utils/Interface";
import { canSelectOptionInSelection, canSelectModuleWithIdInSelections } from "../Utils/ValidationHelpers";
import { generalConfigurationStore } from "./GeneralConfigurationStore";
import { STORE_EXPIRATION_TIME } from "../config";

// use only until id will be loaded dynamically

const hydrate = create({
	storage: localStorage,
	jsonify: true,
});

interface ISelection {
	deliverType?: IDeliverTypeElement,
	options: { [id: string]: IOption[] }
	modules: { [stepId: string]: IModule[] }
	generalModules: { [moduleType: string]: IGeneralModule[] }
	textNotes: { [id: string]: string },
	lastViewedTimestamp?: number
}

export interface IValidationResult {
	valid: boolean,
	reason: {
		modules: IIncompatibleModule[]
	}
}

export class ProductConfigurationStore {
	baseProductId: string;
	apiClientDynamicBaseUrl: APIClient;

	@observable isLoading: boolean = false;

	@observable product?: IProduct;
	@observable modules?: IProductOptions;

	@persist("object") @observable selection: ISelection = {
		options: {},
		modules: {},
		generalModules: {},
		textNotes: {},
		lastViewedTimestamp: new Date().getSeconds()
	};

	// get array with all configurator images
	@computed get getRenderImages() {
		return utilsFunctions.getRenderImages(this)
	}

	@computed get numberOfSelectedMainModules() {
		return this.modules?.steps?.reduce((prevValue, step) => {
			return this.getSelectedMainModulesOfStep(step).length + prevValue
		}, 0) || 0
	}

	@computed get numberOfSelectedMainGeneralModules() {
		return this.modules?.generalStep?.moduleTypes?.reduce((prevValue, moduleType) => {
			return this.getSelectedGeneralModulesOfModuleType(moduleType).length + prevValue
		}, 0) || 0
	}

	@computed get numberOfSelectedModules() {
		return this.numberOfSelectedMainModules + this.numberOfSelectedMainGeneralModules
	}

	@computed get numberOfTotalSelectableModules() {
		// get step count. For each step only one module can be selected
		const stepsCount = this.modules?.steps?.length || 0;

		// get general modules count, only single selection allowed
		const generalStepCount = this.modules?.generalStep?.moduleTypes?.length || 0

		return stepsCount + generalStepCount
	}

	@computed get formattedNumberOfSelectedModules() {
		const selectedModules = String(this.numberOfSelectedModules).padStart(2, "0");
		const totalModulesCount = String(this.numberOfTotalSelectableModules).padStart(2, "0");
		return selectedModules + "/" + totalModulesCount + " " + generalConfigurationStore.configs?.summarySelectedCounterText;
	}

	constructor(apiClientDynamicBaseUrl: APIClient, productId: string) {
		this.baseProductId = productId;
		this.apiClientDynamicBaseUrl = apiClientDynamicBaseUrl;
		this.loadData()
	}

	// set default values
	finishLoading() {
		const deliverTypes = this.modules?.deliverType?.deliverTypes;
		if (deliverTypes && deliverTypes.length > 0) {
			this.selection = { ...this.selection, lastViewedTimestamp: new Date().getSeconds(), deliverType: deliverTypes[0] }
		}

		this.isLoading = false;
	}

	async loadData() {
		this.isLoading = true;
		try {
			Promise.all([
				this.loadProduct(),
				this.loadModules(),
			]).then(_ => {
				// make preselections
				// rehydrate
				return hydrate("productConfigurationStore-" + this.baseProductId, this).then(() => {

					// before finishing loading check if store is older than 7 days, if yes reset selection
					if (this.isStoreExpired()) {
						this.resetSelection()
					}

					this.modules?.steps?.forEach((step) => {
						if (step.requiresSelection) {
							step.modules?.forEach((module) => {

								if (this.selection.modules[step.stepId]) {
									this.selection.modules[step.stepId].forEach((selectedModule) => {
										this.deselectAllModulesOfStep(step);
										this.selectModule(selectedModule, step);
									})
								} else {
									if (module.isPreselected) {
										this.deselectAllModulesOfStep(step);
										this.selectModule(module, step);
									}
								}
							})
						}
					})

					this.finishLoading()
				}).catch(e => {
					console.error(e);
					this.finishLoading()
				})
			});
		} catch (e) {
			console.error(e);
			this.finishLoading()
		}
	}

	isStoreExpired() {
		let storeTimeStamp = this.selection.lastViewedTimestamp;

		// check if storeTimeStamp exist
		if (!storeTimeStamp) {
			return false;
		} else {

			// check if storeTimeStamp is older than 7 days
			const timestamp = storeTimeStamp + STORE_EXPIRATION_TIME;

			if (new Date().getSeconds() > timestamp) {
				return true;
			} else {
				return false
			}
		}
	}

	// load all product data by product id
	async loadProduct() {
		const target = API.getProductById(this.baseProductId);
		return this.apiClientDynamicBaseUrl.requestType<IProduct>(target).then((product) => {
			this.product = product;
		});
	}

	// load all modules data by product id
	async loadModules() {
		const target = API.getModulesByProductId(this.baseProductId);
		return this.apiClientDynamicBaseUrl.requestType<IProductOptions>(target).then((modules) => {
			this.modules = modules;
		});
	}

	validateModuleSelection() {
		const selectedModules = this.selection.modules;
		return Object.keys(selectedModules).find((stepId: any) => selectedModules[stepId].length === 0) !== undefined;
	}

	resetSelection() {
		// reset selection and preselect delivertype
		const deliverTypes = this.modules?.deliverType?.deliverTypes;
		if (deliverTypes && deliverTypes.length > 0) {
			this.selection = { options: {}, lastViewedTimestamp: new Date().getSeconds(), textNotes: {}, generalModules: {}, modules: {}, deliverType: deliverTypes[0] }
		}
	}

	/** OPTIONS */

	selectedModulesHaveRowWidth() {
		for (var key in this.selection.modules) {
			if (this.selection.modules[key].filter(module => (module.moduleFields?.mrRowWidths || []).length > 0).length > 0) {
				return true
			}
		}
		return false
	}

	selectedModulesHaveCoulterWidth() {
		for (var key in this.selection.modules) {
			if (this.selection.modules[key].filter(module => (module.moduleFields?.mrCoulterWidths || []).length > 0).length > 0) {
				return true
			}
		}
		return false
	}

	// check if a given option can be selected
	canSelectCoulterWidthOption(option: IOption) {

		// if selected modules have no coulter width options, opt out
		if (!this.selectedModulesHaveCoulterWidth()) {
			return { valid: false, reason: { modules: [], generalModules: [] } }
		}

		return canSelectOptionInSelection(option, this.selection.modules, (module) => {
			return module.moduleFields?.mrCoulterWidths
		})
	}

	// check if a given option can be selected
	canSelectRowWidthOption(option: IOption) {

		// if selected modules have no row width options, opt out
		if (!this.selectedModulesHaveRowWidth()) {
			return { valid: false, reason: { modules: [], generalModules: [] } }
		}

		return canSelectOptionInSelection(option, this.selection.modules, (module) => {
			return module.moduleFields?.mrRowWidths
		})
	}

	// revalidates the selected options
	revalidateOptions() {
		for (var key in this.selection.options) {
			const options = this.selection.options[key]
			const newOptions: IOption[] = []
			options.forEach(option => {
				if (this.canSelectRowWidthOption(option).valid || this.canSelectCoulterWidthOption(option).valid)
					newOptions.push(option)
			})
			this.selection.options[key] = newOptions
		}
	}

	// resets all selected options, should be called when changing main modules
	@action resetOptions() {
		if (this.selection)
			this.selection.options = {}
	}

	// select deliver type
	@action selectDeliverType(deliverType: IDeliverTypeElement) {
		if (this.selection)
			this.selection = { ...this.selection, deliverType };
	}

	// select option, overwrite selection or allow multiple selection
	@action selectOptionWithId(option: IOption, selectionId: string, singleSelection: boolean = false) {
		if (this.selection) {
			let options = this.selection.options;
			const currentSelection = options[selectionId] || [];

			if (singleSelection) {
				// remove item if already selected
				if (currentSelection.find(o => o.id === option.id)) {
					options[selectionId] = []
				} else {
					// just add item
					options[selectionId] = [option];
				}
			} else {
				// remove or add selection
				options[selectionId] = currentSelection?.find(o => o.id === option.id) ? currentSelection?.filter(o => o.id !== option.id) : currentSelection.concat(option)
			}
			this.selection = { ...this.selection, options }
		}
	}

	/** MODULES */

	// single selects module or deselects module of specific step
	@action selectModule(module: IModule, step: IStep) {
		if (!this.canSelectModule(module, step).valid)
			return

		let modules = this.selection.modules[step.stepId] || []
		// remove module if already selected
		if (modules.find(o => o.id === module.id) !== undefined) {
			this.deselectModule(module, step)
		} else {// just add item, reset row widths and coulter width
			this.selection.modules[step.stepId] = modules.concat([module]);
			this.revalidateOptions() // check if any checkbox options must change
		}
	}

	@action deselectAllModulesOfStep(step: IStep) {
		this.selection.modules[step.stepId] = []
		this.revalidateOptions() // check if any checkbox options must change
	}

	@action deselectModule(module: IModule, step: IStep) {
		let selectedModules = this.selection.modules[step.stepId] || []
		selectedModules = selectedModules.filter((selectedModule) => selectedModule.id !== module.id && (module.addonModules || []).filter(am => am.id === selectedModule.id).length === 0)
		this.selection.modules[step.stepId] = selectedModules
		this.revalidateOptions() // check if any checkbox options must change
	}

	canSelectModule(module: IModule, step?: IStep) {
		// skip validation for current step
		return canSelectModuleWithIdInSelections(module.id, [this.selection.modules, this.selection.generalModules], step?.stepId ? [step.stepId]: undefined)
	}

	isModuleSelected(module: IModule, step: IStep) {
		return this.selection.modules[step.stepId]?.find(m => m.id === module.id) !== undefined
	}


	/** GENERAL MODULES **/

	// single selects general module or deselects general module of specific moduleType
	@action selectGeneralModule(generalModule: IGeneralModule, moduleType: IModuleType) {
		if (!this.canSelectGeneralModule(generalModule).valid)
			return

		let generalModules = this.selection.generalModules[moduleType.termId] || []
		// remove module if already selected
		if (generalModules.find(o => o.id === generalModule.id) !== undefined) {
			this.deselectGeneralModule(generalModule, moduleType)
		} else { // just add item
			this.selection.generalModules[moduleType.termId] = generalModules.concat([generalModule]);
		}
	}

	@action deselectGeneralModule(generalModule: IGeneralModule, moduleType: IModuleType) {
		let selectedModules = this.selection.generalModules[moduleType.termId] || []
		// deselect generalModule and all of it´s add ons
		selectedModules = selectedModules.filter((selectedModule) => selectedModule.id !== generalModule.id && (generalModule.addonModules || []).filter(am => am.id === selectedModule.id).length === 0)
		this.selection.generalModules[moduleType.termId] = selectedModules
	}

	@action deselectAllGeneralModulesOfModuleType(moduleType: IModuleType) {
		this.selection.generalModules[moduleType.termId] = []
	}

	@action clearModuleTypeSelection(moduleType: IModuleType) {
		// reset general module of given modul type
		this.selection.generalModules[moduleType.termId] = []
	}

	isGeneralModuleSelected(generalModule: IGeneralModule, moduleType: IModuleType) {
		return this.selection.generalModules[moduleType.termId]?.find(o => o.id === generalModule.id) !== undefined
	}

	isModuleTypeSelected(moduleType: IModuleType) {
		return this.selection.generalModules[moduleType.termId]?.length > 0
	}


	canSelectGeneralModule(generalModule: IGeneralModule, skipId?: string): IValidationResult {
		return canSelectModuleWithIdInSelections(generalModule.id, [this.selection.generalModules, this.selection.modules], skipId ? [skipId] : undefined)
	}

	textHandler(text: string, textId: string) {
		this.selection.textNotes[textId] = text;
	}

	getReadableModuleSelection() {
		return utilsFunctions.getReadableSelectedModules(this)
	}

	getSelectedGeneralModulesOfModuleType(moduleType: IModuleType) {
		const selectedGeneralModulesOfModuleType = this.selection.generalModules[moduleType.termId] || []
		return selectedGeneralModulesOfModuleType.filter(checkModule => {
			return selectedGeneralModulesOfModuleType.filter(selectedModule => {
				return selectedModule.addonModules?.find(m => m.id === checkModule.id)
			}).length === 0
		})
	}

	getSelectedGeneralSubModulesOfGeneralModuleInModuleType(generalModule: IGeneralModule, moduleType: IModuleType) {
		const selectedGeneralModulesOfModuleType = this.selection.generalModules[moduleType.termId] || []
		return selectedGeneralModulesOfModuleType.filter(checkModule => {
			return generalModule.addonModules?.find(subModule => checkModule.id === subModule.id)
		})
	}

	getSelectedMainModulesOfStep(step: IStep) {
		const selectedModulesOfStep = this.selection.modules[step.stepId] || []
		return selectedModulesOfStep.filter(checkModule => {
			return selectedModulesOfStep.filter(selectedModule => {
				return selectedModule.addonModules?.find(m => m.id === checkModule.id)
			}).length === 0
		})
	}

	getSelectedSubModulesOfModuleInStep(module: IModule, step: IStep) {
		const selectedModulesOfStep = this.selection.modules[step.stepId] || []
		return selectedModulesOfStep.filter(checkModule => {
			return module.addonModules?.find(subModule => checkModule.id === subModule.id)
		})
	}

	getSelectedOptions() {
		return Object.keys(this.selection.options).flatMap(key => {
			return this.selection.options[key]
		})
	}

	getSelectedOptionsForKey(key: string) {
		return Object.keys(this.selection.options).filter(optionKey => {
			return optionKey === key
		}).flatMap(key => {
			return this.selection.options[key]
		})
	}
}
